// src/pages/CartPage.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { getUsShippingUSD } from "../utils/pricing";
import { createCheckoutSession } from "../services/stripeService";
import Navigation from "../components/Common/header/Navigation";
import Footer from "../components/Common/footer/Footer";
import "../styles/Checkout.css";

const CartPage = () => {
    // Pulling the new context variables
    const {
        cart, removeFromCart, updateQuantity,
        cartSubtotal, cartTotal, discount, appliedCode,
        applyPromoCode, removePromoCode,
        hasUnavailableItems, removeUnavailableItems
    } = useCart();

    const { currentUser } = useAuth();
    const { currency, formatPrice, shipsTo, usAvailable, settings } = useCurrency();

    // US shipping is a known flat fee (set in the admin), so show it and include it in the total.
    // UK shipping is still "calculated at checkout" as before.
    const usShipping = currency === "USD" ? getUsShippingUSD(cartSubtotal, settings) : 0;
    const displayTotal = cartTotal + usShipping;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Promo state
    const [promoInput, setPromoInput] = useState("");
    const [promoMessage, setPromoMessage] = useState({ text: "", isError: false });

    // Terms & Guest state
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [guestEmail, setGuestEmail] = useState("");

    const handleQuantityChange = (productId, newQuantity) => {
        updateQuantity(productId, parseInt(newQuantity));
    };

    const handleApplyPromo = () => {
        if (!promoInput) return;
        const result = applyPromoCode(promoInput);
        setPromoMessage({ text: result.message, isError: !result.success });
        if (result.success) setPromoInput("");
    };

    const handleProceedToCheckout = async () => {
        const checkoutEmail = currentUser?.email || guestEmail;

        if (hasUnavailableItems) {
            setError("Some items in your cart aren't available for US delivery. Please remove them to continue.");
            return;
        }

        if (!currentUser && !guestEmail) {
            setError("Please enter your email address to continue.");
            return;
        }

        if (!currentUser && !guestEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            setError("Please enter a valid email address.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            // Note: We are passing appliedCode to Stripe so it knows to discount the session!
            await createCheckoutSession(cart, checkoutEmail, currentUser?.uid || null, appliedCode, currency);
        } catch (err) {
            console.error("Checkout failed:", err);
            setError("Failed to connect to secure checkout. Please ensure the server is running.");
            setLoading(false);
        }
    };

    return (
        <div className="checkout-page">
            <Navigation />
            <div className="container py-5">
                <h2 className="checkout-page-title">Your Cart</h2>
                <div className="checkout-title-divider" />

                {cart.length === 0 ? (
                    <div className="cart-empty">
                        <p style={{ marginBottom: "24px" }}>Your cart is empty</p>
                        <Link to="/shop" className="checkout-btn" style={{ display: "inline-block", width: "auto", padding: "13px 40px" }}>
                            Continue Shopping
                        </Link>
                    </div>
                ) : (
                    <div className="row">
                        <div className="col-md-8">
                            {cart.map((item) => (
                                <div key={item.id} className="cart-item">
                                    <img src={item.image} alt={item.name} className="cart-item-img" />
                                    <div>
                                        <p className="cart-item-name">{item.name}</p>
                                        {item.variantLabel && (
                                            <p style={{ margin: 0, fontSize: "0.75rem", color: "#a0aec0" }}>{item.variantLabel}</p>
                                        )}
                                        {item.unavailable ? (
                                            <p className="cart-item-price" style={{ color: "#e57373" }}>Not available for US delivery</p>
                                        ) : (
                                            <p className="cart-item-price">{formatPrice(item.price)}</p>
                                        )}
                                    </div>
                                    <div className="cart-qty">
                                        <button className="cart-qty-btn" onClick={() => handleQuantityChange(item.id, item.quantity - 1)}>−</button>
                                        <input
                                            type="number"
                                            className="cart-qty-input"
                                            value={item.quantity}
                                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                            min="1"
                                        />
                                        <button className="cart-qty-btn" onClick={() => handleQuantityChange(item.id, item.quantity + 1)}>+</button>
                                    </div>
                                    <span className="cart-item-subtotal">{item.unavailable ? "—" : formatPrice(item.price * item.quantity)}</span>
                                    <button className="cart-remove-btn" onClick={() => removeFromCart(item.id)}>
                                        <i className="fas fa-trash" />
                                    </button>
                                </div>
                            ))}

                            {hasUnavailableItems && (
                                <div style={{ marginTop: "16px", padding: "14px 18px", border: "1px solid rgba(229,115,115,0.4)", background: "rgba(229,115,115,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                                    <span style={{ color: "#e57373", fontSize: "0.85rem" }}>
                                        Some items aren't available for US delivery.
                                    </span>
                                    <button
                                        onClick={removeUnavailableItems}
                                        style={{ background: "transparent", border: "1px solid #e57373", color: "#e57373", padding: "8px 16px", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "1px", cursor: "pointer" }}
                                    >
                                        Remove them
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="col-md-4">
                            <div className="summary-panel">
                                <p className="summary-title">Order Summary</p>
                                {cart.filter(item => !item.unavailable).map(item => (
                                    <div key={item.id} className="summary-item">
                                        <span className="summary-item-name">{item.name} × {item.quantity}</span>
                                        <span>{formatPrice(item.price * item.quantity)}</span>
                                    </div>
                                ))}
                                <hr className="summary-divider" />

                                {/* --- PROMO CODE SECTION --- */}
                                <div className="promo-code-container" style={{ margin: "20px 0", paddingBottom: "20px", borderBottom: "1px solid rgba(212, 175, 55, 0.15)" }}>
                                    {!appliedCode ? (
                                        <>
                                            <div style={{ display: "flex", gap: "10px" }}>
                                                <input
                                                    type="text"
                                                    value={promoInput}
                                                    onChange={(e) => setPromoInput(e.target.value)}
                                                    placeholder="Enter Promo Code"
                                                    style={{
                                                        flex: 1,
                                                        background: "rgba(255,255,255,0.03)",
                                                        border: "1px solid rgba(212, 175, 55, 0.3)",
                                                        color: "#f5f0e8",
                                                        padding: "10px 15px",
                                                        fontFamily: "'Montserrat', sans-serif",
                                                        fontSize: "0.85rem",
                                                        textTransform: "uppercase",
                                                        letterSpacing: "1px",
                                                        outline: "none"
                                                    }}
                                                />
                                                <button
                                                    onClick={handleApplyPromo}
                                                    style={{
                                                        background: "transparent",
                                                        border: "1px solid #d4af37",
                                                        color: "#d4af37",
                                                        padding: "10px 20px",
                                                        fontSize: "0.75rem",
                                                        textTransform: "uppercase",
                                                        cursor: "pointer",
                                                        transition: "all 0.3s"
                                                    }}
                                                    onMouseEnter={(e) => { e.target.style.background = "#d4af37"; e.target.style.color = "#000"; }}
                                                    onMouseLeave={(e) => { e.target.style.background = "transparent"; e.target.style.color = "#d4af37"; }}
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                            {promoMessage.text && (
                                                <p style={{
                                                    marginTop: "10px",
                                                    marginBottom: 0,
                                                    fontSize: "0.75rem",
                                                    color: promoMessage.isError ? "#e57373" : "#4ade80",
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    letterSpacing: "1px"
                                                }}>
                                                    {promoMessage.text}
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <div style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            background: "rgba(212, 175, 55, 0.05)",
                                            border: "1px solid rgba(212, 175, 55, 0.3)",
                                            padding: "10px 15px"
                                        }}>
                                            <span style={{ color: "#d4af37", fontSize: "0.85rem", fontWeight: "700", letterSpacing: "2px" }}>
                                                ✓ {appliedCode} APPLIED
                                            </span>
                                            <button
                                                onClick={removePromoCode}
                                                style={{ background: "none", border: "none", color: "#e57373", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "1px", cursor: "pointer" }}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="summary-item">
                                    <span>Subtotal</span>
                                    <span>{formatPrice(cartSubtotal)}</span>
                                </div>

                                {discount > 0 && (
                                    <div className="summary-item" style={{ color: "#d4af37" }}>
                                        <span>Discount ({appliedCode})</span>
                                        <span>-{formatPrice(discount)}</span>
                                    </div>
                                )}

                                <div className="summary-item">
                                    <span>Shipping</span>
                                    <span>
                                        {currency === "USD"
                                            ? (usShipping === 0 ? "Free" : formatPrice(usShipping))
                                            : "Calculated at checkout"}
                                    </span>
                                </div>
                                <hr className="summary-divider" />
                                <div className="summary-total">
                                    <span>Total</span>
                                    <span>{formatPrice(displayTotal)}</span>
                                </div>
                                {usAvailable && (
                                    <p style={{ margin: "10px 0 0", fontSize: "0.72rem", color: "#a0aec0", letterSpacing: "0.5px" }}>
                                        Prices in {currency} · Delivering to the {shipsTo}. Change currency in the menu bar.
                                    </p>
                                )}

                                {!currentUser && (
                                    <div className="mt-4 mb-3">
                                        <label style={{ fontSize: '0.9rem', color: '#d4af37', marginBottom: '8px', display: 'block' }}>Email Address</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            placeholder="your.email@example.com"
                                            value={guestEmail}
                                            onChange={(e) => setGuestEmail(e.target.value)}
                                            style={{ backgroundColor: '#1a1a1a', border: '1px solid #333', color: '#fff', padding: '12px', borderRadius: '4px' }}
                                        />
                                    </div>
                                )}

                                {currentUser && (
                                    <div className="mt-4 mb-3" style={{ padding: '12px', backgroundColor: '#1a1a1a', borderRadius: '4px', border: '1px solid #333' }}>
                                        <small style={{ color: '#a0aec0', fontSize: '0.75rem', display: 'block' }}>Logged in as</small>
                                        <span style={{ color: '#d4af37', fontSize: '0.9rem' }}>{currentUser.email}</span>
                                    </div>
                                )}

                                <div className="terms-checkbox mt-4 mb-3 text-start">
                                    <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                        <input
                                            type="checkbox"
                                            checked={agreedToTerms}
                                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                                            style={{ marginTop: '5px', accentColor: '#d4af37', width: '16px', height: '16px', flexShrink: 0 }}
                                        />
                                        <span style={{ fontSize: '0.8rem', color: '#a0aec0', lineHeight: '1.6' }}>
                                            I have read and agree to the <Link to="/terms" style={{ color: '#d4af37' }} target="_blank">Terms & Conditions</Link> and <Link to="/privacy" style={{ color: '#d4af37' }} target="_blank">Privacy Policy</Link>.
                                        </span>
                                    </label>
                                </div>

                                {error && <div className="alert alert-danger mt-3" style={{ fontSize: "0.85rem" }}>{error}</div>}

                                <button
                                    className="checkout-btn w-100"
                                    onClick={handleProceedToCheckout}
                                    disabled={loading || !agreedToTerms || hasUnavailableItems}
                                    style={{ opacity: ((!agreedToTerms || hasUnavailableItems) && !loading) ? 0.5 : 1 }}
                                >
                                    {loading ? <span><i className="fas fa-spinner fa-spin me-2"></i> Loading...</span> : <span>Proceed to Checkout</span>}
                                </button>
                                <div className="text-center mt-3" style={{ color: "#a0aec0", fontSize: "0.85rem" }}><i className="fas fa-lock me-1"></i> Secure encryption via Stripe</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
};

export default CartPage;